import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { uploadPhoto, generateApiKey, revokeApiKey, getMyApiKey } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  Camera, Upload, Loader2, Check, X, Image, Key, Copy, RefreshCw, 
  Smartphone, ChevronDown, ChevronUp, Info
} from "lucide-react";

export default function MobileUploadPage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [apiKey, setApiKey] = useState(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [showApiSection, setShowApiSection] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadApiKey();
    }
  }, [isAuthenticated]);

  const loadApiKey = async () => {
    try {
      const res = await getMyApiKey();
      if (res.data.has_key) {
        setApiKey(res.data.api_key);
      }
    } catch (error) {
      console.error("Error loading API key:", error);
    }
  };

  const handleGenerateKey = async () => {
    setLoadingKey(true);
    try {
      const res = await generateApiKey();
      setApiKey(res.data.api_key);
      toast.success("Clé API générée !");
    } catch (error) {
      toast.error("Erreur lors de la génération");
    } finally {
      setLoadingKey(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!window.confirm("Révoquer cette clé API ? Vous devrez en générer une nouvelle.")) return;
    setLoadingKey(true);
    try {
      await revokeApiKey();
      setApiKey(null);
      toast.success("Clé API révoquée");
    } catch (error) {
      toast.error("Erreur lors de la révocation");
    } finally {
      setLoadingKey(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié !");
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    let successCount = 0;

    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      
      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await uploadPhoto(formData);
        successCount++;
        setUploadedPhotos(prev => [...prev, {
          id: res.data?.id || Date.now(),
          filename: file.name,
          preview: URL.createObjectURL(file)
        }]);
      } catch (error) {
        toast.error(`Erreur: ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploadée${successCount > 1 ? 's' : ''} !`);
    }
    
    setUploading(false);
    // Reset inputs
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-secondary pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-secondary pt-20" data-testid="mobile-upload-page">
        <div className="max-w-md mx-auto px-6 py-12 text-center">
          <Smartphone className="h-16 w-16 text-muted-foreground/30 mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold text-primary mb-4">
            Upload Mobile
          </h1>
          <p className="text-muted-foreground mb-6">
            Connectez-vous pour uploader vos photos depuis votre iPhone.
          </p>
          <a href="/connexion">
            <Button className="bg-accent hover:bg-accent/90">Se connecter</Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="mobile-upload-page">
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Camera className="h-8 w-8 text-accent" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-primary mb-2">
            Upload Photos
          </h1>
          <p className="text-muted-foreground text-sm">
            Bonjour {user?.name} ! Uploadez vos photos directement depuis votre iPhone.
          </p>
        </div>

        {/* Upload Buttons */}
        <div className="space-y-4 mb-8">
          {/* Camera capture */}
          <div>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
              id="camera-input"
              disabled={uploading}
            />
            <label htmlFor="camera-input">
              <Button
                asChild
                className="w-full h-14 bg-accent hover:bg-accent/90 text-lg gap-3"
                disabled={uploading}
              >
                <span>
                  {uploading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6" />
                  )}
                  Prendre une photo
                </span>
              </Button>
            </label>
          </div>

          {/* Photo library */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-input"
              disabled={uploading}
            />
            <label htmlFor="file-input">
              <Button
                asChild
                variant="outline"
                className="w-full h-14 text-lg gap-3 border-2"
                disabled={uploading}
              >
                <span>
                  {uploading ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Image className="h-6 w-6" />
                  )}
                  Choisir depuis Photos
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* Uploaded Photos Preview */}
        {uploadedPhotos.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-lg font-semibold text-primary mb-4">
              Photos uploadées ({uploadedPhotos.length})
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {uploadedPhotos.slice(-9).map((photo) => (
                <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                  <img 
                    src={photo.preview} 
                    alt={photo.filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                </div>
              ))}
            </div>
            {uploadedPhotos.length > 9 && (
              <p className="text-center text-sm text-muted-foreground mt-2">
                +{uploadedPhotos.length - 9} autres photos
              </p>
            )}
          </div>
        )}

        {/* API Key Section for Shortcuts/Automation */}
        <div className="border border-border rounded-lg bg-white overflow-hidden">
          <button
            onClick={() => setShowApiSection(!showApiSection)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <div className="flex items-center gap-3">
              <Key className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-primary">Upload automatique</p>
                <p className="text-xs text-muted-foreground">Avec iOS Raccourcis</p>
              </div>
            </div>
            {showApiSection ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          
          {showApiSection && (
            <div className="px-4 pb-4 border-t border-border pt-4">
              <div className="bg-muted/50 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    Utilisez cette clé API avec l'app iOS <strong>Raccourcis</strong> pour 
                    uploader automatiquement vos photos. Créez un raccourci avec l'action 
                    "Obtenir le contenu de l'URL" vers notre API.
                  </p>
                </div>
              </div>

              {apiKey ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      Votre clé API
                    </label>
                    <div className="flex gap-2">
                      <Input 
                        value={apiKey} 
                        readOnly 
                        className="font-mono text-xs"
                      />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => copyToClipboard(apiKey)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">
                      URL d'upload
                    </label>
                    <div className="flex gap-2">
                      <Input 
                        value={`${process.env.REACT_APP_BACKEND_URL}/api/mobile/upload`}
                        readOnly 
                        className="font-mono text-xs"
                      />
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/mobile/upload`)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-destructive border-destructive/50"
                    onClick={handleRevokeKey}
                    disabled={loadingKey}
                  >
                    {loadingKey ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
                    Révoquer la clé
                  </Button>
                </div>
              ) : (
                <Button 
                  className="w-full bg-primary"
                  onClick={handleGenerateKey}
                  disabled={loadingKey}
                >
                  {loadingKey ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Key className="h-4 w-4 mr-2" />
                  )}
                  Générer une clé API
                </Button>
              )}

              {/* Shortcut instructions */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs font-medium text-primary mb-2">Configuration du Raccourci iOS :</p>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
                  <li>Ouvrez l'app <strong>Raccourcis</strong></li>
                  <li>Créez un nouveau raccourci</li>
                  <li>Ajoutez "Sélectionner des photos"</li>
                  <li>Ajoutez "Obtenir le contenu de l'URL"</li>
                  <li>Méthode: POST, URL: (copiez l'URL ci-dessus)</li>
                  <li>Corps: Formulaire, ajoutez "file" (photo) et "api_key" (votre clé)</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        {/* Tips */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Les métadonnées EXIF (date, lieu GPS) sont automatiquement extraites.
          </p>
        </div>
      </div>
    </div>
  );
}
